import { useState, useEffect } from 'react';
import { FaTags, FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import supabase from '../../supabaseClient';
import { useAuth } from '../../hooks/useAuth';

const CategoryManagement = () => {
  // Estado para armazenar a lista de categorias
  const [categories, setCategories] = useState([]);
  // Estado para o carregamento de dados
  const [loading, setLoading] = useState(true);
  // Estado para mensagens de erro
  const [error, setError] = useState(null);

  // Estado para o formulário de adicionar/editar categoria
  const [formData, setFormData] = useState({
    id: null,
    nome: '',
    descricao: '',
    ativo: true
  });
  
  // Estado para controlar se estamos editando ou adicionando
  const [isEditing, setIsEditing] = useState(false);
  // Estado para controlar o modal
  const [showModal, setShowModal] = useState(false);
  // Obter contexto de autenticação
  const { hasPermission } = useAuth();
  
  // Carregar categorias do Supabase
  const fetchCategories = async () => {
    try {
      setLoading(true);
      console.log('Iniciando carregamento de categorias...');
      
      const { data, error } = await supabase
        .from('caex_categorias')
        .select('*')
        .order('id', { ascending: true });
      
      if (error) {
        console.error('Erro ao buscar categorias:', error);
        throw error;
      }
      
      console.log('Categorias carregadas:', data);
      
      // Força a conversão e normalização das categorias
      const normalizedCategories = Array.isArray(data) ? data.map(cat => ({
        id: Number(cat.id),
        nome: String(cat.nome || `Categoria ${cat.id}`),
        descricao: cat.descricao || '',
        ativo: Boolean(cat.ativo),
        created_at: cat.created_at,
        updated_at: cat.updated_at
      })) : [];
      
      console.log('Categorias normalizadas:', normalizedCategories);
      
      if (normalizedCategories.length === 0) {
        console.warn('Nenhuma categoria encontrada no banco de dados.');
      }
      
      setCategories(normalizedCategories);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      setError('Não foi possível carregar as categorias de produtos.');
      setCategories([]);
      setLoading(false);
    }
  };

  // Carregar categorias quando o componente for montado
  useEffect(() => {
    fetchCategories();
  }, []);
  
  // Estilos para o modal flutuante
  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem',
      backdropFilter: 'blur(5px)'
    },
    content: {
      position: 'relative',
      width: '100%',
      maxWidth: '650px',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'white',
      borderRadius: '20px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      overflow: 'hidden',
      animationName: 'fadeInUp',
      animationDuration: '0.4s',
      animationTimingFunction: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
      animationFillMode: 'both'
    },
    header: {
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
      padding: '1.5rem 2rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0
    },
    closeButton: {
      width: '36px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      border: 'none',
      background: 'white',
      color: '#6c757d',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
    },
    title: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: '#1a1a1a',
      margin: '0',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem'
    },
    body: {
      padding: '2rem',
      overflowY: 'auto',
      flex: '1 1 auto'
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      padding: '1.5rem 2rem',
      borderTop: '1px solid rgba(0, 0, 0, 0.05)',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      flexShrink: 0
    },
    footerButton: {
      padding: '0.75rem 1.5rem',
      borderRadius: '50px',
      fontSize: '0.9rem',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    },
    cancelButton: {
      border: '1px solid #dee2e6',
      background: 'white',
      color: '#495057',
      marginRight: '0.75rem'
    },
    submitButton: {
      border: 'none',
      background: '#212529',
      color: 'white',
      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)'
    },
    formGroup: {
      marginBottom: '1.5rem'
    },
    formLabel: {
      display: 'block',
      marginBottom: '0.5rem',
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#495057'
    },
    formInput: {
      width: '100%',
      padding: '0.75rem 1rem',
      border: '1px solid #dee2e6',
      borderRadius: '10px',
      fontSize: '0.9rem',
      color: '#495057',
      transition: 'all 0.3s ease'
    },
    requiredField: {
      color: '#dc3545',
      marginLeft: '0.25rem'
    },
    requiredNotice: {
      fontSize: '0.8rem',
      color: '#dc3545',
      marginBottom: '1rem'
    }
  };

  // Adicionar estilo de animação ao carregar o componente
  useEffect(() => {
    // Criar elemento de estilo para a animação
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes fadeInUp {
        from { 
          opacity: 0; 
          transform: translateY(30px);
        }
        to { 
          opacity: 1; 
          transform: translateY(0);
        }
      }
      
      .form-input {
        width: 100%;
        padding: 0.75rem 1rem;
        border: 1px solid #dee2e6;
        border-radius: 10px;
        font-size: 0.9rem;
        color: #495057;
        transition: all 0.3s ease;
      }
      
      .form-input:focus {
        outline: none;
        border-color: #212529;
        box-shadow: 0 0 0 3px rgba(33, 37, 41, 0.1);
      }
      
      .form-label {
        display: block;
        margin-bottom: 0.5rem;
        font-size: 0.9rem;
        font-weight: 600;
        color: #495057;
      }
      
      .form-group {
        margin-bottom: 1.5rem;
      }
      
      .modal-close-btn:hover {
        background-color: #f8f9fa;
        color: #212529;
        transform: rotate(90deg);
      }
      
      .btn-cancel:hover {
        background-color: #f8f9fa;
      }
      
      .btn-submit:hover {
        background-color: #343a40;
        transform: translateY(-2px);
        box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);
      }
      
      /* Estilos para a tabela moderna */
      .modern-table-card {
        background-color: #fff;
        border-radius: 15px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
        overflow: hidden;
        margin-bottom: 2rem;
        border: 1px solid rgba(0, 0, 0, 0.05);
      }
      
      .modern-card-header {
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        padding: 1.25rem 1.5rem;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
      }
      
      .modern-card-title {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin: 0;
        font-size: 1.15rem;
        font-weight: 600;
        color: #212529;
      }
      
      .card-title-icon {
        color: #6c757d;
      }
      
      .modern-card-body {
        padding: 0;
      }
      
      .modern-table-container {
        overflow-x: auto;
      }
      
      .modern-table {
        width: 100%;
        border-collapse: collapse;
      }
      
      .modern-table th,
      .modern-table td {
        padding: 1rem 1.5rem;
        text-align: left;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
      }
      
      .modern-table th {
        background-color: #f8f9fa;
        font-weight: 600;
        color: #495057;
        font-size: 0.9rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      
      .modern-table tr:last-child td {
        border-bottom: none;
      }
      
      .modern-table tr:hover {
        background-color: rgba(0, 0, 0, 0.01);
      }
      
      .category-name {
        font-weight: 600;
        color: #212529;
      }
      
      .text-right {
        text-align: right;
      }
    `;
    document.head.appendChild(style);
    
    // Limpar ao desmontar
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Impedir o scroll quando o modal estiver aberto
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showModal]);
  
  // Adicionar efeito para rolar o modal para o topo quando for aberto
  useEffect(() => {
    if (showModal) {
      // Resetar a posição de scroll do modal quando aberto
      setTimeout(() => {
        const modalBody = document.querySelector('.modal-scrollable-body');
        if (modalBody) {
          modalBody.scrollTop = 0;
        }
      }, 100);
    }
  }, [showModal]);

  // Manipular mudanças nos campos do formulário
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Abrir o modal para adicionar uma nova categoria
  const handleAddNew = () => {
    setFormData({
      id: null,
      nome: '',
      descricao: '',
      ativo: true
    });
    setIsEditing(false);
    setShowModal(true);
  };

  // Abrir o modal para editar uma categoria existente
  const handleEdit = (category) => {
    setFormData({
      ...category
    });
    
    setIsEditing(true);
    setShowModal(true);
  };

  // Excluir uma categoria
  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta categoria?')) {
      try {
        const { error } = await supabase
          .from('caex_categorias')
          .delete()
          .eq('id', id);
        
        if (error) throw error;
        
        // Atualizar a lista de categorias
        setCategories(categories.filter(category => category.id !== id));
      } catch (error) {
        console.error('Erro ao excluir categoria:', error);
        alert('Não foi possível excluir a categoria. Por favor, tente novamente.');
      }
    }
  };

  // Salvar o formulário (adicionar nova ou atualizar existente)
  const handleSubmit = async () => {
    try {
      // Validar dados do formulário
      if (!formData.nome) {
        alert('Por favor, preencha o nome da categoria.');
        return;
      }

      // Adicionar timestamp de atualização
      const updatedData = {
        ...formData,
        updated_at: new Date()
      };
      
      if (isEditing) {
        // Atualizar categoria existente
        const { error } = await supabase
          .from('caex_categorias')
          .update(updatedData)
          .eq('id', formData.id);
        
        if (error) throw error;
        
        // Atualizar a lista de categorias localmente
        setCategories(categories.map(category => 
          category.id === formData.id ? updatedData : category
        ));
      } else {
        // Para novas categorias, remover o campo ID para permitir auto-incremento
        const { id, ...dataWithoutId } = updatedData;
        console.log('Dados para inserção (sem ID):', dataWithoutId);
        
        // Adicionar nova categoria
        const { data, error } = await supabase
          .from('caex_categorias')
          .insert([dataWithoutId])
          .select();
        
        if (error) throw error;
        
        console.log('Categoria adicionada com sucesso:', data);
        
        // Adicionar a nova categoria à lista
        setCategories([...categories, data[0]]);
      }
      
      // Fechar o modal e resetar o formulário
      setShowModal(false);
      setFormData({
        id: null,
        nome: '',
        descricao: '',
        ativo: true
      });
    } catch (error) {
      console.error('Erro ao salvar categoria:', error);
      alert(`Não foi possível salvar a categoria. Erro: ${error.message || 'Desconhecido'}`);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-hub">
          <div className="dashboard-header">
            <div className="dashboard-title-section">
              <h1 className="dashboard-main-title">
                <FaTags className="dashboard-title-icon" />
                Gestão de Categorias
              </h1>
            </div>
          </div>
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-hub">
          <div className="dashboard-header">
            <div className="dashboard-title-section">
              <h1 className="dashboard-main-title">
                <FaTags className="dashboard-title-icon" />
                Gestão de Categorias
              </h1>
            </div>
          </div>
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative my-4" role="alert">
            <strong className="font-bold">Erro: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-hub">
        {/* Header Section */}
        <div className="dashboard-header">
          <div className="dashboard-title-section">
            <h1 className="dashboard-main-title">
              <FaTags className="dashboard-title-icon" />
              Gestão de Categorias
            </h1>
            <p className="dashboard-subtitle">
              Gerenciamento de categorias para produtos
            </p>
          </div>
        </div>

        {/* Área de botão */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-start', 
          alignItems: 'center', 
          marginBottom: '20px',
          width: '100%'
        }}>
          {/* Botão no canto esquerdo */}
          {hasPermission('all') && (
            <button 
              onClick={handleAddNew}
              className="bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800 transition-all flex items-center gap-2"
              style={{ boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}
            >
              <FaPlus />
              Adicionar Categoria
            </button>
          )}
        </div>

        {/* Tabela de categorias */}
        <div className="modern-table-card" style={{ animation: 'fadeInUp 0.6s ease-out' }}>
          <div className="modern-card-header">
            <h5 className="modern-card-title">
              <FaTags className="card-title-icon" />
              Lista de Categorias
            </h5>
          </div>
          <div className="modern-card-body">
            {categories.length === 0 ? (
              <div className="bg-gray-100 border border-gray-300 text-gray-700 px-4 py-8 rounded-lg text-center my-4">
                <p className="text-xl">Nenhuma categoria encontrada.</p>
                <p className="mt-2">Crie uma nova categoria para começar.</p>
              </div>
            ) : (
              <div className="modern-table-container">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nome</th>
                      <th>Descrição</th>
                      <th>Status</th>
                      <th>Última Atualização</th>
                      <th className="text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((category) => (
                      <tr key={category.id}>
                        <td>{category.id}</td>
                        <td className="category-name">{category.nome}</td>
                        <td>{category.descricao ? category.descricao.substring(0, 50) + (category.descricao.length > 50 ? '...' : '') : '-'}</td>
                        <td>
                          <span className="modern-badge" style={{ 
                            backgroundColor: category.ativo ? '#28a745' : '#dc3545',
                            color: 'white',
                            padding: '0.4rem 0.8rem',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {category.ativo ? 'Ativa' : 'Inativa'}
                          </span>
                        </td>
                        <td>{new Date(category.updated_at).toLocaleDateString('pt-BR')}</td>
                        <td className="text-right">
                          {hasPermission('all') && (
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => handleEdit(category)}
                                style={{
                                  ...modalStyles.footerButton,
                                  ...modalStyles.cancelButton
                                }}
                                className="btn-cancel"
                              >
                                <FaEdit className="mr-1" />
                                Editar
                              </button>

                              <button
                                onClick={() => handleDelete(category.id)}
                                style={{
                                  ...modalStyles.footerButton,
                                  ...modalStyles.submitButton,
                                  background: '#dc3545' // vermelho elegante
                                }}
                                className="btn-submit"
                              >
                                <FaTrash className="mr-1" />
                                Excluir
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Modal para adicionar/editar categoria */}
        {showModal && (
          <div style={modalStyles.overlay}>
            <div style={modalStyles.content}>
              <div style={modalStyles.header}>
                <h2 style={modalStyles.title}>
                  {isEditing 
                    ? <FaEdit style={{color: '#6c757d'}} /> 
                    : <FaPlus style={{color: '#6c757d'}} />
                  }
                  {isEditing ? 'Editar Categoria' : 'Nova Categoria'}
                </h2>
                <button 
                  onClick={() => setShowModal(false)}
                  style={modalStyles.closeButton}
                  className="modal-close-btn"
                  aria-label="Fechar"
                >
                  ✕
                </button>
              </div>
              
              <div style={modalStyles.body} className="modal-scrollable-body">
                <div className="form-group">
                  <label className="form-label" htmlFor="nome">
                    Nome da Categoria <span style={modalStyles.requiredField}>*</span>
                  </label>
                  <input
                    type="text"
                    id="nome"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Digite o nome da categoria"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label" htmlFor="descricao">
                    Descrição
                  </label>
                  <textarea
                    id="descricao"
                    name="descricao"
                    value={formData.descricao || ''}
                    onChange={handleChange}
                    rows="4"
                    className="form-input"
                    placeholder="Digite a descrição da categoria"
                  ></textarea>
                </div>
                
                <div className="switch-container">
                  <label className="switch">
                    <input
                      type="checkbox"
                      id="ativo"
                      name="ativo"
                      checked={formData.ativo}
                      onChange={handleChange}
                    />
                    <span className="slider"></span>
                  </label>
                  <div>
                    <span className="switch-label">Categoria ativa</span>
                    <p className="switch-description">
                      Determina se a categoria está disponível no sistema
                    </p>
                  </div>
                </div>
                
                <div style={modalStyles.requiredNotice}>
                  * Campos obrigatórios
                </div>
              </div>
              
              <div style={modalStyles.footer}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{...modalStyles.footerButton, ...modalStyles.cancelButton}}
                  className="btn-cancel"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  style={{...modalStyles.footerButton, ...modalStyles.submitButton}}
                  className="btn-submit"
                >
                  {isEditing ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryManagement;